import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Field, reduxForm } from "redux-form";
import RichTextMarkdown from "../../../Components/Forms/wysiwyg";
import RichTextEditor from "react-rte";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const TemplateIntroAndChapters = (props) => {
  const { handleSubmit, pristine, submitting, onBack } = props;
  const [chapters, setChapters] = useState(props.initialValues?.chapters || []);
  const [introductionRTE, setIntroductionRTE] = useState(
    props.initialValues?.introduction
      ? RichTextEditor.createValueFromString(props.initialValues.introduction, 'html')
      : RichTextEditor.createEmptyValue()
  );

  useEffect(() => {
    // Initialize RTE value for introduction
    if (props.initialValues?.introduction) {
      setIntroductionRTE(RichTextEditor.createValueFromString(props.initialValues.introduction, 'html'));
    }

    if (props.initialValues?.chapters) {
      // Initialize RTE values for existing chapters
      const chaptersWithRTE = props.initialValues.chapters.map(ch => ({
        ...ch,
        descriptionRTE: ch.description
          ? RichTextEditor.createValueFromString(ch.description, 'html')
          : RichTextEditor.createEmptyValue()
      }));
      setChapters(chaptersWithRTE);
    }
  }, [props.initialValues]);

  const handleAddChapter = () => {
    setChapters([
      ...chapters,
      {
        title: "",
        description: "",
        descriptionRTE: RichTextEditor.createEmptyValue(),
        sort_order: chapters.length
      }
    ]);
  };

  const handleUpdateChapter = (index, field, value) => {
    const newChapters = [...chapters];
    if (field === 'description') {
      // For WYSIWYG, we expect an RTE value object
      if (value && value.toString) {
        newChapters[index][field] = value.toString('html');
        newChapters[index].descriptionRTE = value;
      } else {
        newChapters[index][field] = value;
      }
    } else {
      newChapters[index][field] = value;
    }
    setChapters(newChapters);
  };

  const handleRemoveChapter = (index) => {
    if (window.confirm("Weet je zeker dat je dit hoofdstuk wilt verwijderen?")) {
      const newChapters = chapters.filter((_, i) => i !== index);
      // Herorder sort_order
      newChapters.forEach((chapter, i) => {
        chapter.sort_order = i;
      });
      setChapters(newChapters);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newChapters = Array.from(chapters);
    const [reorderedItem] = newChapters.splice(result.source.index, 1);
    newChapters.splice(result.destination.index, 0, reorderedItem);

    // Update sort_order
    newChapters.forEach((chapter, index) => {
      chapter.sort_order = index;
    });

    setChapters(newChapters);
  };

  const onSubmit = (values) => {
    // Check if all chapters have titles (only if there are chapters)
    if (chapters.length > 0) {
      const invalidChapters = chapters.filter(ch => !ch.title || ch.title.trim() === '');
      if (invalidChapters.length > 0) {
        alert("Alle hoofdstukken moeten een titel hebben.");
        return;
      }
    }

    // Clean up chapters - remove RTE objects before submit
    const cleanChapters = chapters.map(ch => {
      const { descriptionRTE, ...rest } = ch;
      return rest;
    });

    // Voeg chapters en introduction toe aan de form values
    const submitValues = {
      ...values,
      introduction: introductionRTE.toString('html'),
      chapters: cleanChapters
    };

    props.onSubmit(submitValues);
  };

  return (
    <div className="box box-default">
      <form onSubmit={props.handleSubmit(onSubmit)}>
        <div className="box-body">
          <div className="row">
            <div className="col-md-10 offset-md-1">
              {/* Introduction sectie */}
              <div className="mb-5">
                <h4>Introductie</h4>
                <p className="text-muted mb-3">
                  Schrijf een introductie voor het inspectie rapport. Dit wordt getoond aan het begin van het rapport.
                </p>
                <div className="form-group">
                  <label className="font-weight-bold">Introductie Tekst</label>
                  <RichTextEditor
                    className="wysiwyg-editor"
                    value={introductionRTE}
                    onChange={setIntroductionRTE}
                    placeholder="Voeg een introductie toe voor het rapport..."
                  />
                  <small className="text-muted">
                    Deze tekst wordt getoond aan het begin van het inspectie rapport
                  </small>
                </div>
              </div>

              {/* Hoofdstukken sectie */}
              <h4>Hoofdstukken</h4>
              <p className="text-muted mb-4">
                Definieer de hoofdstukken van het inspectie rapport.
                Elk hoofdstuk vormt een sectie in het rapport.
              </p>

              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Hoofdstukken ({chapters.length})</h5>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleAddChapter}
                  >
                    <i className="fas fa-plus"></i> Hoofdstuk Toevoegen
                  </button>
                </div>

                {chapters.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle"></i> Nog geen hoofdstukken toegevoegd.
                    Voeg hoofdstukken toe om je rapport te structureren.
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="chapters">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {chapters.map((chapter, index) => (
                            <Draggable
                              key={index}
                              draggableId={`chapter-${index}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`card mb-3 ${snapshot.isDragging ? "shadow-lg" : ""}`}
                                >
                                  <div className="card-body">
                                    <div className="d-flex align-items-start">
                                      <div
                                        {...provided.dragHandleProps}
                                        className="drag-handle me-3"
                                        style={{ cursor: "grab" }}
                                      >
                                        <i className="fas fa-grip-vertical text-muted"></i>
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="row">
                                          <div className="col-md-8">
                                            <div className="form-group">
                                              <label className="font-weight-bold">
                                                Hoofdstuk {index + 1} - Titel *
                                              </label>
                                              <input
                                                type="text"
                                                className="form-control"
                                                value={chapter.title}
                                                onChange={(e) =>
                                                  handleUpdateChapter(index, "title", e.target.value)
                                                }
                                                placeholder="bijv. Veiligheidscontrole"
                                                required
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-4 text-end">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger mt-4"
                                              onClick={() => handleRemoveChapter(index)}
                                            >
                                              <i className="fas fa-trash"></i> Verwijder
                                            </button>
                                          </div>
                                        </div>
                                        <div className="form-group mt-3">
                                          <label className="text-muted">
                                            Omschrijving / Toelichting
                                          </label>
                                          <RichTextEditor
                                            className="wysiwyg-editor"
                                            value={chapter.descriptionRTE || RichTextEditor.createEmptyValue()}
                                            onChange={(value) =>
                                              handleUpdateChapter(index, "description", value)
                                            }
                                            placeholder="Voeg een omschrijving toe voor dit hoofdstuk..."
                                          />
                                          <small className="text-muted">
                                            Deze tekst wordt getoond bij het uitvoeren van de inspectie
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            </div>
          </div>

          {/* Navigatie buttons */}
          <div className="d-flex justify-content-between mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={props.onBack}
            >
              <i className="fas fa-arrow-left"></i> Vorige Stap
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={props.submitting}
            >
              Volgende Stap: Items per Hoofdstuk <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

let ReduxTemplateIntroAndChapters = reduxForm({
  form: "template_intro_chapters",
  enableReinitialize: true
})(TemplateIntroAndChapters);

ReduxTemplateIntroAndChapters = connect((state) => ({
  initialValues: state.zwembadinspecties.inspectionTemplate || {
    introduction: "",
    chapters: []
  }
}))(ReduxTemplateIntroAndChapters);

export default ReduxTemplateIntroAndChapters;
